import React, {useState, useEffect} from 'react';
import config from '../config';
import { getBffHeaders } from '../utils/apiHeaders';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import miaFlowAuth from '../auth/MiaFlowAuth';
import '../pages/Settings.css';
import './Advanced.css';

const _AI_API_PREFIX = 'api/catalog';

/**
 * Advanced Settings Component
 * Contains advanced configuration options
 */
const Advanced = () => {
  const [k8sDynamicMode, setK8sDynamicMode] = useState(false);
  const [savingK8sMode, setSavingK8sMode] = useState(false);
  const [versions, setVersions] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [showResourceFeatures, setShowResourceFeatures] = useState(false);
  const [savingResourceFeatures, setSavingResourceFeatures] = useState(false);
  const [showAdvancedCTA, setShowAdvancedCTA] = useState(false);
  const [savingAdvancedCTA, setSavingAdvancedCTA] = useState(false);
  const [showToolsPage, setShowToolsPage] = useState(false);
  const [savingToolsPage, setSavingToolsPage] = useState(false);
  const [showPopularTemplates, setShowPopularTemplates] = useState(false);
  const [savingPopularTemplates, setSavingPopularTemplates] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [savingSearch, setSavingSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [savingFavorites, setSavingFavorites] = useState(false);
  const [enableCollaboration, setEnableCollaboration] = useState(false);
  const [savingCollaboration, setSavingCollaboration] = useState(false);
  const [showDashboardTab, setShowDashboardTab] = useState(false);
  const [savingDashboardTab, setSavingDashboardTab] = useState(false);
  const [enableConsolidatedGitButton, setEnableConsolidatedGitButton] = useState(false);
  const [savingConsolidatedGitButton, setSavingConsolidatedGitButton] = useState(false);
  const [enableMcpApps, setEnableMcpApps] = useState(false);
  const [savingMcpApps, setSavingMcpApps] = useState(false);
  const [showRequirementsTab, setShowRequirementsTab] = useState(false);
  const [savingRequirementsTab, setSavingRequirementsTab] = useState(false);
  const [enableOpenTelemetry, setEnableOpenTelemetry] = useState(false);
  const [savingOpenTelemetry, setSavingOpenTelemetry] = useState(false);
  // Default Playbook state
  const [defaultChatPlaybookId, setDefaultChatPlaybookId] = useState('');
  const [defaultCodePlaybookId, setDefaultCodePlaybookId] = useState('');
  const [availablePlaybooks, setAvailablePlaybooks] = useState([]);
  const [loadingPlaybooks, setLoadingPlaybooks] = useState(false);

  useEffect(() => {
    // Load K8s Dynamic Mode setting (default: false)
    const k8sModeSaved = localStorage.getItem('k8sDynamicMode');
    if (k8sModeSaved !== null) {
      setK8sDynamicMode(k8sModeSaved === 'true');
    }

    // Load feature toggle settings (default: true for resource features)
    const resourceFeaturesSaved = localStorage.getItem('showResourceFeatures');
    if (resourceFeaturesSaved !== null) {
      setShowResourceFeatures(resourceFeaturesSaved === 'true');
    } else {
      setShowResourceFeatures(true);
    }

    // Load advanced CTA toggle settings (default: false)
    const advancedCTASaved = localStorage.getItem('showAdvancedCTA');
    if (advancedCTASaved !== null) {
      setShowAdvancedCTA(advancedCTASaved === 'true');
    }

    // Load tools page toggle settings (default: true)
    const toolsPageSaved = localStorage.getItem('showToolsPage');
    if (toolsPageSaved !== null) {
      setShowToolsPage(toolsPageSaved === 'true');
    } else {
      setShowToolsPage(true);
    }

    // Load popular templates toggle settings (default: false)
    const popularTemplatesSaved = localStorage.getItem('showPopularTemplates');
    if (popularTemplatesSaved !== null) {
      setShowPopularTemplates(popularTemplatesSaved === 'true');
    }

    // Load search toggle settings (default: false)
    const searchSaved = localStorage.getItem('showSearch');
    if (searchSaved !== null) {
      setShowSearch(searchSaved === 'true');
    }

    // Load notifications toggle settings (default: false)
    const notificationsSaved = localStorage.getItem('showNotifications');
    if (notificationsSaved !== null) {
      setShowNotifications(notificationsSaved === 'true');
    }

    // Load favorites toggle settings (default: false)
    const favoritesSaved = localStorage.getItem('showFavorites');
    if (favoritesSaved !== null) {
      setShowFavorites(favoritesSaved === 'true');
    }

    // Load collaboration toggle settings (default: false)
    const collaborationSaved = localStorage.getItem('enableCollaboration');
    if (collaborationSaved !== null) {
      setEnableCollaboration(collaborationSaved === 'true');
    }

    // Load dashboard tab toggle settings (default: false)
    const dashboardTabSaved = localStorage.getItem('showDashboardTab');
    if (dashboardTabSaved !== null) {
      setShowDashboardTab(dashboardTabSaved === 'true');
    }

    // Load consolidated git button toggle settings (default: false)
    const consolidatedGitButtonSaved = localStorage.getItem('ENABLE_CONSOLIDATED_GIT_BUTTON');
    if (consolidatedGitButtonSaved !== null) {
      setEnableConsolidatedGitButton(consolidatedGitButtonSaved === 'true');
    }

    // Load MCP Apps toggle settings (default: false)
    const mcpAppsSaved = localStorage.getItem('ENABLE_MCP_APPS');
    if (mcpAppsSaved !== null) {
      setEnableMcpApps(mcpAppsSaved === 'true');
    }

    // Load requirements tab toggle settings (default: false)
    const requirementsTabSaved = localStorage.getItem('showRequirementsTab');
    if (requirementsTabSaved !== null) {
      setShowRequirementsTab(requirementsTabSaved === 'true');
    }

    // Load OpenTelemetry toggle settings (default: false)
    const openTelemetrySaved = localStorage.getItem('ENABLE_OPENTELEMETRY');
    if (openTelemetrySaved !== null) {
      setEnableOpenTelemetry(openTelemetrySaved === 'true');
    }

    // Load default playbook settings
    const savedChatPlaybook = localStorage.getItem('DEFAULT_CHAT_PLAYBOOK_ID');
    if (savedChatPlaybook) setDefaultChatPlaybookId(savedChatPlaybook);
    const savedCodePlaybook = localStorage.getItem('DEFAULT_CODE_PLAYBOOK_ID');
    if (savedCodePlaybook) setDefaultCodePlaybookId(savedCodePlaybook);

    // Load component versions
    fetchVersions();
    // Load available playbooks for default selection
    fetchPlaybooks();
  }, []);

  const fetchPlaybooks = async () => {
    const tenantId = miaFlowAuth.getSelectedTenant()?.tenantId;
    if (!tenantId) return;
    try {
      setLoadingPlaybooks(true);
      const baseUrl = config.bffUrl || window.location.origin;
      const qs = new URLSearchParams({ tenantId, page: '1', perPage: '100' });
      const response = await getAuthorizedFetch()(`${baseUrl}/${_AI_API_PREFIX}/ai-playbooks?${qs}`, {
        headers: getBffHeaders(),
      });
      const result = await response.json();
      if (result.success && result.data?.items) {
        setAvailablePlaybooks(result.data.items);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load playbooks for default selection:', error);
    } finally {
      setLoadingPlaybooks(false);
    }
  };

  const handleDefaultChatPlaybookChange = (id) => {
    setDefaultChatPlaybookId(id);
    if (id) {
      localStorage.setItem('DEFAULT_CHAT_PLAYBOOK_ID', id);
    } else {
      localStorage.removeItem('DEFAULT_CHAT_PLAYBOOK_ID');
    }
  };

  const handleDefaultCodePlaybookChange = (id) => {
    setDefaultCodePlaybookId(id);
    if (id) {
      localStorage.setItem('DEFAULT_CODE_PLAYBOOK_ID', id);
    } else {
      localStorage.removeItem('DEFAULT_CODE_PLAYBOOK_ID');
    }
  };

  const fetchVersions = async () => {
    try {
      setLoadingVersions(true);
      const baseUrl = config.bffUrl || window.location.origin;
      const response = await getAuthorizedFetch()(`${baseUrl}/api/versions`, {
        headers: getBffHeaders(),
      });
      const result = await response.json();
      
      if (result.success) {
        setVersions(result.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load versions:', error);
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleK8sDynamicModeChange = async (enabled) => {
    setK8sDynamicMode(enabled);
    localStorage.setItem('k8sDynamicMode', enabled.toString());
    
    try {
      setSavingK8sMode(true);
      // eslint-disable-next-line no-console
      console.log(`🔧 K8s Dynamic Mode ${enabled ? 'enabled' : 'disabled'} - will use ${enabled ? 'dynamic K8s pods' : 'single shared container'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save K8s Dynamic Mode setting:', error);
    } finally {
      setSavingK8sMode(false);
    }
  };

  const handleResourceFeaturesChange = async (enabled) => {
    setShowResourceFeatures(enabled);
    localStorage.setItem('showResourceFeatures', enabled.toString());
    
    try {
      setSavingResourceFeatures(true);
      // Dispatch custom event to update sidebar
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showResourceFeatures: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Resource Features ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Resource Features setting:', error);
    } finally {
      setSavingResourceFeatures(false);
    }
  };

  const handleAdvancedCTAChange = async (enabled) => {
    setShowAdvancedCTA(enabled);
    localStorage.setItem('showAdvancedCTA', enabled.toString());
    
    try {
      setSavingAdvancedCTA(true);
      // Dispatch custom event to update home page
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showAdvancedCTA: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Advanced Call-to-Actions ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Advanced CTA setting:', error);
    } finally {
      setSavingAdvancedCTA(false);
    }
  };

  const handleToolsPageChange = async (enabled) => {
    setShowToolsPage(enabled);
    localStorage.setItem('showToolsPage', enabled.toString());
    
    try {
      setSavingToolsPage(true);
      // Dispatch custom event to update sidebar
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showToolsPage: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Tools Page ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Tools Page setting:', error);
    } finally {
      setSavingToolsPage(false);
    }
  };

  const handlePopularTemplatesChange = async (enabled) => {
    setShowPopularTemplates(enabled);
    localStorage.setItem('showPopularTemplates', enabled.toString());
    
    try {
      setSavingPopularTemplates(true);
      // Dispatch custom event to update home page
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showPopularTemplates: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Popular Templates ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Popular Templates setting:', error);
    } finally {
      setSavingPopularTemplates(false);
    }
  };

  const handleSearchChange = async (enabled) => {
    setShowSearch(enabled);
    localStorage.setItem('showSearch', enabled.toString());
    
    try {
      setSavingSearch(true);
      // Dispatch custom event to update sidebar
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showSearch: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Search ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Search setting:', error);
    } finally {
      setSavingSearch(false);
    }
  };

  const handleNotificationsChange = async (enabled) => {
    setShowNotifications(enabled);
    localStorage.setItem('showNotifications', enabled.toString());
    
    try {
      setSavingNotifications(true);
      // Dispatch custom event to update sidebar
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showNotifications: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Notifications ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Notifications setting:', error);
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleFavoritesChange = async (enabled) => {
    setShowFavorites(enabled);
    localStorage.setItem('showFavorites', enabled.toString());
    
    try {
      setSavingFavorites(true);
      // Dispatch custom event to update projects page
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showFavorites: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Favorites ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Favorites setting:', error);
    } finally {
      setSavingFavorites(false);
    }
  };
  const handleCollaborationChange = async (enabled) => {
    setEnableCollaboration(enabled);
    localStorage.setItem('enableCollaboration', enabled.toString());
    
    try {
      setSavingCollaboration(true);
      // Dispatch custom event to update collaboration features
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { enableCollaboration: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Real-time Collaboration ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Collaboration setting:', error);
    } finally {
      setSavingCollaboration(false);
    }
  };

  const handleDashboardTabChange = async (enabled) => {
    setShowDashboardTab(enabled);
    localStorage.setItem('showDashboardTab', enabled.toString());

    try {
      setSavingDashboardTab(true);
      // Dispatch custom event to update sidebar
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showDashboardTab: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Dashboard Tab ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Dashboard Tab setting:', error);
    } finally {
      setSavingDashboardTab(false);
    }
  };

  const handleConsolidatedGitButtonChange = async (enabled) => {
    setEnableConsolidatedGitButton(enabled);
    localStorage.setItem('ENABLE_CONSOLIDATED_GIT_BUTTON', enabled.toString());
    
    try {
      setSavingConsolidatedGitButton(true);
      // Dispatch custom event to update git controls
      window.dispatchEvent(new Event('featureFlagChanged'));
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { enableConsolidatedGitButton: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Consolidated Git Button ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Consolidated Git Button setting:', error);
    } finally {
      setSavingConsolidatedGitButton(false);
    }
  };

  const handleMcpAppsChange = async (enabled) => {
    setEnableMcpApps(enabled);
    localStorage.setItem('ENABLE_MCP_APPS', enabled.toString());
    
    try {
      setSavingMcpApps(true);
      // Dispatch custom event to update MCP Apps feature
      window.dispatchEvent(new Event('featureFlagChanged'));
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { enableMcpApps: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 MCP Apps ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save MCP Apps setting:', error);
    } finally {
      setSavingMcpApps(false);
    }
  };

  const handleRequirementsTabChange = async (enabled) => {
    setShowRequirementsTab(enabled);
    localStorage.setItem('showRequirementsTab', enabled.toString());
    
    try {
      setSavingRequirementsTab(true);
      // Dispatch custom event to update sidebar
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { showRequirementsTab: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 Requirements Tab ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save Requirements Tab setting:', error);
    } finally {
      setSavingRequirementsTab(false);
    }
  };

  const handleOpenTelemetryChange = async (enabled) => {
    setEnableOpenTelemetry(enabled);
    localStorage.setItem('ENABLE_OPENTELEMETRY', enabled.toString());

    try {
      setSavingOpenTelemetry(true);
      window.dispatchEvent(new CustomEvent('featureToggleChanged', { detail: { enableOpenTelemetry: enabled } }));
      // eslint-disable-next-line no-console
      console.log(`🔧 OpenTelemetry ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to save OpenTelemetry setting:', error);
    } finally {
      setSavingOpenTelemetry(false);
    }
  };

  return (
    <div className="advanced-content">
      <div className="settings-advanced-card">
        <div className="settings-field">
          <div className="settings-field-header">
            <label className="settings-field-label" htmlFor="k8s-dynamic-mode">
              {'K8s Dynamic Mode'}
            </label>
            <span className="settings-field-badge">{'Advanced'}</span>
          </div>
          <p className="settings-field-description">
            {'Enable Kubernetes-based dynamic container spawning. When enabled, each project+user combination gets a dedicated K8s pod with automatic cleanup after 30 minutes of inactivity. When disabled (default), uses a single shared container with port-based isolation.'}
          </p>
          <div className="settings-field-input-group">
            <label className="settings-toggle">
              <input
                checked={k8sDynamicMode}
                onChange={(e) => handleK8sDynamicModeChange(e.target.checked)}
                type="checkbox"
              />
              <span className="settings-toggle-slider" />
            </label>
            <span className="settings-field-toggle-label">
              {k8sDynamicMode ? 'Dynamic K8s Pods (Enabled)' : 'Single Container (Default)'}
            </span>
            {savingK8sMode && (
              <span className="settings-field-saving">{'Saving...'}</span>
            )}
          </div>
          <div className="settings-field-info">
            <p style={{fontSize: '0.85rem', color: '#888', marginTop: '0.5rem'}}>
              <strong>{'Single Container Mode:'}</strong>{' Uses /api/bff/:containerName/* endpoints'}<br />
              <strong>{'K8s Dynamic Mode:'}</strong>{' Uses /api/bff/preview/* endpoints with per-user pods'}
            </p>
          </div>
        </div>

        {/* Feature Toggles Section */}
        <div className="settings-field" style={{marginTop: '2rem'}}>
          <div className="settings-field-header">
            <label className="settings-field-label">
              {'Feature Toggles'}
            </label>
            <span className="settings-field-badge">{'Advanced'}</span>
          </div>
          <p className="settings-field-description">
            {'Customize which features are visible in the sidebar. Toggle off to hide specific sections.'}
          </p>
          
          <div className="feature-toggles-container">
            {/* Toggle 1: Show Resource Features */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showResourceFeatures}
                    onChange={(e) => handleResourceFeaturesChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Show Resource Features'}
                </span>
                {savingResourceFeatures && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When disabled, hides: Discover and Templates from sidebar'}
              </p>
            </div>

            {/* Toggle 2: Show Tools Page */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showToolsPage}
                    onChange={(e) => handleToolsPageChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Show Tools Page'}
                </span>
                {savingToolsPage && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When disabled, hides: Tools item from sidebar'}
              </p>
            </div>

            {/* Toggle 3: Show Popular Templates */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showPopularTemplates}
                    onChange={(e) => handlePopularTemplatesChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Show Popular Templates'}
                </span>
                {savingPopularTemplates && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When disabled, hides: Popular Templates section from home page'}
              </p>
            </div>

            {/* Toggle 4: Show Advanced Call-to-Actions */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showAdvancedCTA}
                    onChange={(e) => handleAdvancedCTAChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Show Advanced Call-to-Actions'}
                </span>
                {savingAdvancedCTA && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When disabled, hides: Import from Git, Modernize Legacy App, and Browse Projects cards'}
              </p>
            </div>

            {/* Toggle 5: Show Search */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showSearch}
                    onChange={(e) => handleSearchChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Show Search'}
                </span>
                {savingSearch && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When disabled, hides: Search item from sidebar'}
              </p>
            </div>

            {/* Toggle 6: Show Notifications */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showNotifications}
                    onChange={(e) => handleNotificationsChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Show Notifications'}
                </span>
                {savingNotifications && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When disabled, hides: Notification bell icon from sidebar footer'}
              </p>
            </div>

            {/* Toggle 7: Show Favorites */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showFavorites}
                    onChange={(e) => handleFavoritesChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Show Favorites'}
                </span>
                {savingFavorites && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When disabled, hides: Starred and Shared with me from sidebar, and favorite star buttons on project cards'}
              </p>
            </div>

            {/* Toggle 8: Real-time Collaboration */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={enableCollaboration}
                    onChange={(e) => handleCollaborationChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Enable Real-time Collaboration'}
                </span>
                {savingCollaboration && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When enabled: Shows live cursors, user presence, and collaboration panel. Allows multiple users and AI agents to work together in real-time (Google Docs style).'}
              </p>
            </div>

            {/* Toggle 9: Dashboard Tab */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showDashboardTab}
                    onChange={(e) => handleDashboardTabChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Enable Dashboard Tab'}
                </span>
                {savingDashboardTab && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When enabled: Shows the Dashboard tab in the Chat\'s.'}
              </p>
            </div>

            {/* Toggle 10: Consolidated Git Button */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={enableConsolidatedGitButton}
                    onChange={(e) => handleConsolidatedGitButtonChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Enable Consolidated Git Button'}
                </span>
                {savingConsolidatedGitButton && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When enabled: Replaces separate Pull/Push/Pipeline buttons with a single status-aware button that opens a comprehensive modal for all git operations (Changes, Branch Management, Pipeline, Quick Actions).'}
              </p>
            </div>

            {/* Toggle 11: MCP Apps */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={enableMcpApps}
                    onChange={(e) => handleMcpAppsChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Enable MCP Apps'}
                </span>
                {savingMcpApps && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When enabled: Allows AI tools to render interactive HTML-based user interfaces (MCP Apps) directly in chat messages for enhanced data visualization, dashboards, and interactive components.'}
              </p>
            </div>

            {/* Toggle 12: Requirements Tab */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={showRequirementsTab}
                    onChange={(e) => handleRequirementsTabChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Enable Requirements Tab'}
                </span>
                {savingRequirementsTab && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When enabled: Shows the Requirements tab in the Chat\'s for managing content quality, completeness, feature readiness, and regulatory alignment.'}
              </p>
            </div>

            {/* Toggle 13: OpenTelemetry */}
            <div className="feature-toggle-item">
              <div className="settings-field-input-group">
                <label className="settings-toggle">
                  <input
                    checked={enableOpenTelemetry}
                    onChange={(e) => handleOpenTelemetryChange(e.target.checked)}
                    type="checkbox"
                  />
                  <span className="settings-toggle-slider" />
                </label>
                <span className="settings-field-toggle-label" style={{fontWeight: 500}}>
                  {'Enable OpenTelemetry'}
                </span>
                {savingOpenTelemetry && (
                  <span className="settings-field-saving">{'Saving...'}</span>
                )}
              </div>
              <p className="feature-toggle-description">
                {'When enabled: Activates distributed tracing (OTLP, Google Cloud Trace). Requires OTEL_ENABLED=true on the backend.'}
              </p>
            </div>

          </div>
        </div>

        {/* Default Playbooks Section */}
        <div className="settings-field" style={{marginTop: '2rem'}}>
          <div className="settings-field-header">
            <label className="settings-field-label">
              {'Default Playbooks'}
            </label>
            <span className="settings-field-badge">{'Experience'}</span>
          </div>
          <p className="settings-field-description">
            {'Choose which playbook is automatically applied when opening a new Chat or Code session. These replace the built-in AI instructions for a focused, context-aware experience.'}
          </p>

          {loadingPlaybooks ? (
            <div style={{padding: '0.75rem 0', color: '#888', fontSize: '0.875rem'}}>{'Loading playbooks...'}</div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '1.25rem', marginTop: '1rem'}}>
              {/* Default Chat Playbook */}
              <div>
                <label style={{display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem'}}>
                  {'Default Chat Playbook'}
                </label>
                <p style={{fontSize: '0.8rem', color: '#888', margin: '0 0 0.5rem 0'}}>
                  {'Applied automatically when opening /compose (Chat mode) with no active playbook.'}
                </p>
                <select
                  style={{
                    width: '100%', maxWidth: '420px', padding: '0.45rem 0.75rem',
                    borderRadius: '6px', border: '1px solid var(--border-color, #ddd)',
                    background: 'var(--input-bg, #fff)', color: 'inherit', fontSize: '0.875rem'
                  }}
                  value={defaultChatPlaybookId}
                  onChange={(e) => handleDefaultChatPlaybookChange(e.target.value)}
                >
                  <option value={''}>{'— None (use built-in instructions) —'}</option>
                  {availablePlaybooks.map(p => (
                    <option key={p.itemId} value={p.itemId}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Default Code Playbook */}
              <div>
                <label style={{display: 'block', fontWeight: 500, marginBottom: '0.4rem', fontSize: '0.875rem'}}>
                  {'Default Code Playbook'}
                </label>
                <p style={{fontSize: '0.8rem', color: '#888', margin: '0 0 0.5rem 0'}}>
                  {'Applied automatically when opening /chat (Code mode) with no active playbook.'}
                </p>
                <select
                  style={{
                    width: '100%', maxWidth: '420px', padding: '0.45rem 0.75rem',
                    borderRadius: '6px', border: '1px solid var(--border-color, #ddd)',
                    background: 'var(--input-bg, #fff)', color: 'inherit', fontSize: '0.875rem'
                  }}
                  value={defaultCodePlaybookId}
                  onChange={(e) => handleDefaultCodePlaybookChange(e.target.value)}
                >
                  <option value={''}>{'— None (use built-in instructions) —'}</option>
                  {availablePlaybooks.map(p => (
                    <option key={p.itemId} value={p.itemId}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Component Versions Section */}
        <div className="settings-field" style={{marginTop: '2rem'}}>
          <div className="settings-field-header">
            <label className="settings-field-label">
              {'Component Versions'}
            </label>
            <span className="settings-field-badge">{'Info'}</span>
          </div>
          <p className="settings-field-description">
            {'Current versions of all mia-flow components. Updated automatically on each commit and push.'}
          </p>
          
          {loadingVersions ? (
            <div style={{padding: '1rem', textAlign: 'center', color: '#888'}}>
              {'Loading versions...'}
            </div>
          ) : versions && versions.components ? (
            <div style={{marginTop: '1rem', overflowX: 'auto', maxWidth: '100%'}}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.8rem',
                tableLayout: 'fixed'
              }}>
                <thead>
                  <tr style={{
                    borderBottom: '2px solid #e0e0e0',
                    textAlign: 'left'
                  }}>
                    <th style={{padding: '0.4rem 0.3rem', fontWeight: 600, width: '28%'}}>{'Component'}</th>
                    <th style={{padding: '0.4rem 0.3rem', fontWeight: 600, width: '10%'}}>{'Ver'}</th>
                    <th style={{padding: '0.4rem 0.3rem', fontWeight: 600, width: '15%'}}>{'Type'}</th>
                    <th style={{padding: '0.4rem 0.3rem', fontWeight: 600, width: '12%'}}>{'Commit'}</th>
                    <th style={{padding: '0.4rem 0.3rem', fontWeight: 600, width: '35%'}}>{'Updated'}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(versions.components).map(([name, info]) => (
                    <tr key={name} style={{
                      borderBottom: '1px solid #f0f0f0'
                    }}>
                      <td style={{padding: '0.4rem 0.3rem', fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {name}
                      </td>
                      <td style={{padding: '0.4rem 0.3rem', fontFamily: 'monospace', color: 'inherit', fontSize: '0.75rem'}}>
                        {info.version}
                      </td>
                      <td style={{padding: '0.4rem 0.3rem'}}>
                        <span style={{
                          padding: '0.15rem 0.35rem',
                          borderRadius: '3px',
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                          display: 'inline-block',
                          backgroundColor: info.type === 'frontend' ? '#e3f2fd' :
                                          info.type === 'backend' ? '#f3e5f5' :
                                          info.type === 'bff' ? '#fff3e0' :
                                          info.type === 'documentation' ? '#e8f5e9' : '#f5f5f5',
                          color: info.type === 'frontend' ? '#1565c0' :
                                 info.type === 'backend' ? '#6a1b9a' :
                                 info.type === 'bff' ? '#e65100' :
                                 info.type === 'documentation' ? '#2e7d32' : '#616161'
                        }}>
                          {info.type}
                        </span>
                      </td>
                      <td style={{padding: '0.4rem 0.3rem', fontFamily: 'monospace', fontSize: '0.75rem', color: 'inherit'}}>
                        {info.lastCommit || 'N/A'}
                      </td>
                      <td style={{padding: '0.4rem 0.3rem', fontSize: '0.7rem', color: 'inherit', whiteSpace: 'nowrap'}}>
                        {info.lastCommitDate ? new Date(info.lastCommitDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {versions.lastUpdated && (
                <p style={{
                  marginTop: '1rem',
                  fontSize: '0.85rem',
                  color: 'var(--palette-common-grey-600, #666)',
                  textAlign: 'right'
                }}>
                  {'Last sync: '}{new Date(versions.lastUpdated).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <div style={{padding: '1rem', textAlign: 'center', color: '#888'}}>
              {'No version information available'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Advanced;
